import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";
import { RootState } from "../../../../redux/store";

import { FaseBarDragItem, VariazioneBarDragItem } from "./schedulazioneGanttTypes";

import GanttHeader, {
    generaSettimane,
    getLgSettimana,
    getTotaleGiorni,
    SENZA_OPERATORE_ID,
} from "./GanttHeader";
import GanttDropCell from "./GanttDropCell";
import GanttFrazioneBar, { calcolaAltezzaFrazione } from "./GanttFrazioneBar";
import GanttVariazioneBar, { ALTEZZA_VARIAZIONE } from "./GanttVariazioneBar";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import {FaseFrazione, FrazioneOdP} from "../frazioniOdPSlice";
import {CaricoRepartiPanoramica, CaricoRepartoDettaglio, GiornoCalendario} from "../caricoRepartiSlice";
import { VariazioneDisponibilita } from "../variazioniDisponibilitaSlice";
import { Reparto } from "../../../anagrafiche/reparti/repartiSlice";
import { Operatore } from "../../personale/operatoriSlice";

// ============================================
// CONSTANTS
// ============================================

/** Gap verticale tra frazioni */
const GAP_FRAZIONI = 4;

/** Gap verticale tra placeholder e tra placeholder e frazioni */
const GAP_VARIAZIONI = 2;

/** Altezza minima dell'area gantt (quando non ci sono frazioni) */
const ALTEZZA_MINIMA_AREA = 200;

// ============================================
// INTERFACES
// ============================================

interface SchedulazioneGanttTimelineProps {
    dataDa: DateTime;
    numeroSettimane: 2 | 4 | 6;
    onOdPDrop: (odpId: number, data: string) => void;
    onFaseMove: (faseFrazioneId: number, nuovaDataDa: string, nuovaDataA: string) => void;
    onFaseResize: (faseFrazioneId: number, nuovaDataDa: string | null, nuovaDataA: string | null) => void;
    onFrazioneMove: (frazioneId: number, nuovaDataInizio: string) => void;
    onFaseClick?: (faseFrazioneId: number) => void;
    onFrazioneDelete: (frazioneToDelete: FrazioneOdP) => void;
    onFaseDelete: (faseFrazioneToDelete: FaseFrazione) => void;
    onFaseEdit: (faseFrazione: FaseFrazione) => void;
    odpAttivoId?: number;
    onOdPSelect?: (odpId: number | undefined) => void;
    repartiSelezionati?: number[];
    operatoriSelezionati?: number[];
    onVariazioneCreate?: (data: string) => void;
    onVariazioneMove?: (variazioneId: number, nuovaDataDa: string, nuovaDataA: string) => void;
    onVariazioneResize?: (variazioneId: number, nuovaDataDa: string | null, nuovaDataA: string | null) => void;
    onVariazioneClick?: (variazione: VariazioneDisponibilita) => void;
}

// ============================================
// UTILITIES
// ============================================

interface Lane {
    dataFineUltima: DateTime;
    odpIds: Set<number>;
    altezzaMax: number;
}

/**
 * Greedy lane packing: piazza ciascuna frazione nella prima lane libera
 * dall'alto, con preferenza per lane che già ospitano lo stesso OdP
 * (tie-break sul "buco minore" = dataFineUltima massima tra i preferiti).
 * Output: offsetTop calcolato come somma cumulativa delle altezze delle
 * lane precedenti (le lane possono avere altezze diverse).
 */
const calcolaLayoutFrazioni = (
    frazioni: FrazioneOdP[]
): { frazione: FrazioneOdP; offsetTop: number; altezza: number }[] => {
    if (frazioni.length === 0) return [];

    type FrazioneConMeta = {
        frazione: FrazioneOdP;
        dataInizio: DateTime;
        dataFine: DateTime;
        altezza: number;
    };

    const conMeta: FrazioneConMeta[] = frazioni.map((f) => ({
        frazione: f,
        dataInizio: getDataInizioFrazione(f),
        dataFine: getDataFineFrazione(f),
        altezza: calcolaAltezzaFrazione(f.fasi || []),
    }));

    conMeta.sort((a, b) => {
        const diff = a.dataInizio.toMillis() - b.dataInizio.toMillis();
        if (diff !== 0) return diff;
        return (a.frazione.id ?? 0) - (b.frazione.id ?? 0);
    });

    const lanes: Lane[] = [];
    const assegnazioni: { meta: FrazioneConMeta; laneIndex: number }[] = [];

    for (const meta of conMeta) {
        const odpId = meta.frazione.odp;

        const candidati: number[] = [];
        for (let i = 0; i < lanes.length; i++) {
            if (lanes[i].dataFineUltima < meta.dataInizio) candidati.push(i);
        }

        const preferiti = odpId != null
            ? candidati.filter((i) => lanes[i].odpIds.has(odpId))
            : [];

        let laneIndex: number;
        if (preferiti.length > 0) {
            laneIndex = preferiti.reduce(
                (best, i) => (lanes[i].dataFineUltima > lanes[best].dataFineUltima ? i : best),
                preferiti[0],
            );
        } else if (candidati.length > 0) {
            laneIndex = candidati[0];
        } else {
            laneIndex = lanes.length;
            lanes.push({
                dataFineUltima: meta.dataFine,
                odpIds: new Set(odpId != null ? [odpId] : []),
                altezzaMax: meta.altezza,
            });
            assegnazioni.push({ meta, laneIndex });
            continue;
        }

        const lane = lanes[laneIndex];
        lane.dataFineUltima = meta.dataFine;
        if (odpId != null) lane.odpIds.add(odpId);
        if (meta.altezza > lane.altezzaMax) lane.altezzaMax = meta.altezza;
        assegnazioni.push({ meta, laneIndex });
    }

    const topLane: number[] = [];
    let cursore = GAP_FRAZIONI;
    for (const lane of lanes) {
        topLane.push(cursore);
        cursore += lane.altezzaMax + GAP_FRAZIONI;
    }

    return assegnazioni.map(({ meta, laneIndex }) => ({
        frazione: meta.frazione,
        offsetTop: topLane[laneIndex],
        altezza: meta.altezza,
    }));
};

/**
 * Estrae la data inizio minima dalle fasi di una frazione
 */
const getDataInizioFrazione = (frazione: FrazioneOdP): DateTime => {
    const fasi = frazione.fasi || [];
    const fasiConDate = fasi.filter((f) => f.data_inizio_pianificata);

    if (fasiConDate.length === 0) {
        return DateTime.now();
    }

    const date = fasiConDate.map((f) =>
        DateTime.fromISO(f.data_inizio_pianificata as string)
    );

    return DateTime.min(...date) ?? DateTime.now();
};

/**
 * Estrae la data fine massima dalle fasi di una frazione
 */
const getDataFineFrazione = (frazione: FrazioneOdP): DateTime => {
    const fasi = frazione.fasi || [];
    const fasiConDate = fasi.filter((f) => f.data_fine_pianificata);

    if (fasiConDate.length === 0) {
        return DateTime.now();
    }

    const date = fasiConDate.map((f) =>
        DateTime.fromISO(f.data_fine_pianificata as string)
    );

    return DateTime.max(...date) ?? DateTime.now();
};

// ============================================
// COMPONENT
// ============================================

const SchedulazioneGanttTimeline = ({
                                        dataDa,
                                        numeroSettimane,
                                        onOdPDrop,
                                        onFaseMove,
                                        onFaseResize,
                                        onFrazioneMove,
                                        onFaseClick,
                                        onFrazioneDelete,
                                        onFaseDelete,
                                        onFaseEdit,
                                        odpAttivoId,
                                        onOdPSelect,
                                        repartiSelezionati,
                                        operatoriSelezionati,
                                        onVariazioneCreate,
                                        onVariazioneMove,
                                        onVariazioneResize,
                                        onVariazioneClick,
                                    }: SchedulazioneGanttTimelineProps) => {
    const theme = useTheme();

    // ============================================
    // SELECTORS
    // ============================================

    const frazioni = useSelector<RootState, FrazioneOdP[]>(
        (state) => state.frazioniOdPState?.frazioniOdPList?.results || []
    );

    const caricoPanoramica = useSelector<RootState, CaricoRepartiPanoramica | null>(
        (state) => state.caricoRepartiState?.panoramica || null
    );
    const calendario = caricoPanoramica?.calendario || [];

    const caricoDettaglio = useSelector<RootState, { [repartoId: number]: CaricoRepartoDettaglio }>(
        (state) => state.caricoRepartiState?.dettaglio || {}
    );

    const operatori = useSelector<RootState, Operatore[]>(
        (state) => state.operatoriState?.operatoriList?.results || []
    );
    const reparti = useSelector<RootState, Reparto[]>(
        (state) => state.repartiState?.repartiList?.results || []
    );

    const variazioniByRange = useSelector<RootState, { [k: string]: VariazioneDisponibilita[] }>(
        (state) => state.variazioniDisponibilitaState?.byRange || {}
    );
    const ultimoRange = useSelector<RootState, string | undefined>(
        (state) => state.variazioniDisponibilitaState?.ultimoRange
    );
    const variazioni: VariazioneDisponibilita[] = ultimoRange
        ? variazioniByRange[ultimoRange] || []
        : [];

    // ============================================
    // CALCOLI
    // ============================================

    const settimane = useMemo(
        () => generaSettimane(dataDa, numeroSettimane),
        [dataDa, numeroSettimane]
    );

    const lgSettimana = getLgSettimana(numeroSettimane);
    const totaleGiorni = getTotaleGiorni(numeroSettimane);
    const dataInizioGantt = dataDa.startOf("week");

    // Mappa data -> giorno calendario per lookup veloce
    const calendarioMap = useMemo(() => {
        const map: { [data: string]: GiornoCalendario } = {};
        calendario.forEach((g) => {
            map[g.data] = g;
        });
        return map;
    }, [calendario]);

    // Filtra frazioni per reparti e/o operatori selezionati (se presenti)
    const frazioniFiltrate = useMemo(() => {
        let risultato = frazioni;
        if (repartiSelezionati && repartiSelezionati.length > 0) {
            risultato = risultato.filter((frazione) =>
                (frazione.fasi || []).some((fase) =>
                    fase.reparto && repartiSelezionati.includes(fase.reparto)
                )
            );
        }
        if (operatoriSelezionati && operatoriSelezionati.length > 0) {
            const includiSenzaOperatore = operatoriSelezionati.includes(SENZA_OPERATORE_ID);
            risultato = risultato.filter((frazione) =>
                (frazione.fasi || []).some((fase) =>
                    (fase.operatore && operatoriSelezionati.includes(fase.operatore))
                    || (!fase.operatore && includiSenzaOperatore)
                )
            );
        }
        return risultato;
    }, [frazioni, repartiSelezionati, operatoriSelezionati]);

    // Layout frazioni: ordine verticale e altezze
    const layoutFrazioni = useMemo(
        () => calcolaLayoutFrazioni(frazioniFiltrate),
        [frazioniFiltrate]
    );

    // ============================================
    // VARIAZIONI: filtro + layout in cima
    // ============================================

    const repartoByOperatoreId = useMemo(() => {
        const m: { [k: number]: number | undefined } = {};
        operatori.forEach((op) => {
            if (op.id != null) m[op.id] = op.reparto ?? undefined;
        });
        return m;
    }, [operatori]);

    const coloreByRepartoId = useMemo(() => {
        const m: { [k: number]: string | undefined } = {};
        reparti.forEach((r) => {
            if (r.id != null) m[r.id] = r.colore;
        });
        return m;
    }, [reparti]);

    const variazioniFiltrate = useMemo(() => {
        const filtroRep = repartiSelezionati && repartiSelezionati.length > 0;
        const filtroOp = operatoriSelezionati && operatoriSelezionati.length > 0;
        return variazioni.filter((v) => {
            // Filtro reparto: variazione passa se reparto∈filtri OR operatore.reparto∈filtri
            if (filtroRep) {
                const opReparto = v.operatore ? repartoByOperatoreId[v.operatore] : undefined;
                const passaReparto =
                    repartiSelezionati!.includes(v.reparto) ||
                    (opReparto != null && repartiSelezionati!.includes(opReparto));
                if (!passaReparto) return false;
            }
            if (filtroOp) {
                const includeSenza = operatoriSelezionati!.includes(SENZA_OPERATORE_ID);
                const passaOp =
                    (v.operatore != null && operatoriSelezionati!.includes(v.operatore)) ||
                    (v.operatore == null && includeSenza);
                if (!passaOp) return false;
            }
            return true;
        });
    }, [variazioni, repartiSelezionati, operatoriSelezionati, repartoByOperatoreId]);

    /** Stack semplice: una lane per variazione, in alto. */
    const layoutVariazioni = useMemo(() => {
        let top = GAP_VARIAZIONI;
        const out: { variazione: VariazioneDisponibilita; top: number }[] = [];
        variazioniFiltrate.forEach((v) => {
            out.push({ variazione: v, top });
            top += ALTEZZA_VARIAZIONE + GAP_VARIAZIONI;
        });
        return out;
    }, [variazioniFiltrate]);

    const altezzaBandaVariazioni = useMemo(() => {
        if (layoutVariazioni.length === 0) return 0;
        const ultima = layoutVariazioni[layoutVariazioni.length - 1];
        return ultima.top + ALTEZZA_VARIAZIONE + GAP_VARIAZIONI;
    }, [layoutVariazioni]);

    // Altezza totale dell'area frazioni + variazioni.
    // Con il greedy packing l'ordine dell'array non riflette la posizione
    // verticale, quindi cerco il massimo bottom tra tutte le lane.
    const altezzaTotale = useMemo(() => {
        const altezzaFrazioni = layoutFrazioni.length === 0
            ? ALTEZZA_MINIMA_AREA
            : Math.max(...layoutFrazioni.map((l) => l.offsetTop + l.altezza)) + GAP_FRAZIONI;
        return Math.max(ALTEZZA_MINIMA_AREA, altezzaBandaVariazioni + altezzaFrazioni);
    }, [layoutFrazioni, altezzaBandaVariazioni]);

    // Helper posizionamento orizzontale di una variazione sul Gantt.
    // Il Gantt mostra solo i giorni lavorativi (lun-ven): contiamo i giorni
    // con `weekday <= 5`, scartando weekend. Stessa logica di GanttFaseBar.
    const calcolaLeftWidth = (vDataDa: string, vDataA: string) => {
        const dataDaDt = DateTime.fromISO(vDataDa);
        const dataADt = DateTime.fromISO(vDataA);
        // Ultimo venerdì = lunedì + (numeroSettimane − 1) settimane + 4 giorni
        const dataFineGantt = dataInizioGantt.plus({ days: (numeroSettimane - 1) * 7 + 4 });

        if (dataADt < dataInizioGantt || dataDaDt > dataFineGantt) {
            return { isVisible: false, left: "0%", width: "0%" };
        }

        const clampedDa = dataDaDt < dataInizioGantt ? dataInizioGantt : dataDaDt;
        const clampedA = dataADt > dataFineGantt ? dataFineGantt : dataADt;

        // Giorni lavorativi dall'inizio del Gantt fino al giorno PRIMA di clampedDa
        let giorniDallInizio = 0;
        let cursore = dataInizioGantt;
        while (cursore < clampedDa) {
            if (cursore.weekday <= 5) giorniDallInizio++;
            cursore = cursore.plus({ days: 1 });
        }

        // Durata in giorni lavorativi nell'intervallo [clampedDa, clampedA]
        let giorniDurata = 0;
        cursore = clampedDa;
        while (cursore <= clampedA) {
            if (cursore.weekday <= 5) giorniDurata++;
            cursore = cursore.plus({ days: 1 });
        }
        if (giorniDurata === 0) {
            return { isVisible: false, left: "0%", width: "0%" };
        }

        const leftPct = (giorniDallInizio / totaleGiorni) * 100;
        const widthPct = (giorniDurata / totaleGiorni) * 100;

        return { isVisible: true, left: `${leftPct}%`, width: `${widthPct}%` };
    };

    // ============================================
    // HANDLERS
    // ============================================

    const handleOdPDrop = (odpId: number, data: string) => {
        onOdPDrop(odpId, data);
    };

    const handleFaseMove = (draggedItem: FaseBarDragItem, nuovaData: string) => {
        const dataDaOriginale = DateTime.fromISO(draggedItem.dataDa);
        const dataAOriginale = DateTime.fromISO(draggedItem.dataA);
        const durataGiorni = dataAOriginale.diff(dataDaOriginale, "days").days;

        // const nuovaDataA = nuovaData;
        // const nuovaDataDa = DateTime.fromISO(nuovaData)
        //                            .minus({ days: durataGiorni })
        //                            .toFormat("yyyy-MM-dd");

        const nuovaDataDa = nuovaData;
        const nuovaDataA = DateTime.fromISO(nuovaData)
                                   .plus({ days: durataGiorni })
                                   .toFormat("yyyy-MM-dd");

        onFaseMove(draggedItem.faseFrazioneId, nuovaDataDa, nuovaDataA);
    };

    const handleFrazioneMove = (frazioneId: number, nuovaDataInizio: string) => {
        onFrazioneMove(frazioneId, nuovaDataInizio);
    };

    const handleFaseResize = (draggedItem: FaseBarDragItem, nuovaData: string) => {
        if (draggedItem.resizeEdge === "left") {
            onFaseResize(draggedItem.faseFrazioneId, nuovaData, null);
        } else if (draggedItem.resizeEdge === "right") {
            onFaseResize(draggedItem.faseFrazioneId, null, nuovaData);
        }
    };

    const handleVariazioneMove = (item: VariazioneBarDragItem, nuovaData: string) => {
        const dataDaOriginale = DateTime.fromISO(item.dataDa);
        const dataAOriginale = DateTime.fromISO(item.dataA);
        const durataGiorni = Math.floor(dataAOriginale.diff(dataDaOriginale, "days").days);
        const nuovaDataDa = nuovaData;
        const nuovaDataA = DateTime.fromISO(nuovaData)
            .plus({ days: durataGiorni })
            .toFormat("yyyy-MM-dd");
        onVariazioneMove?.(item.variazioneId, nuovaDataDa, nuovaDataA);
    };

    const handleVariazioneResize = (item: VariazioneBarDragItem, nuovaData: string) => {
        if (item.resizeEdge === "left") {
            onVariazioneResize?.(item.variazioneId, nuovaData, null);
        } else if (item.resizeEdge === "right") {
            onVariazioneResize?.(item.variazioneId, null, nuovaData);
        }
    };

    // ============================================
    // RENDER
    // ============================================

    return (
        <Box
            sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                overflow: "auto",
                backgroundColor: theme.palette.background.paper,
            }}
        >
            {/* Header con settimane e giorni */}
            <GanttHeader
                dataDa={dataDa}
                numeroSettimane={numeroSettimane}
                calendario={calendario}
                repartiSelezionati={repartiSelezionati}
                operatoriSelezionati={operatoriSelezionati}
                caricoDettaglio={caricoDettaglio}
            />

            {/* Area corpo gantt */}
            <Box sx={{ position: "relative", minHeight: altezzaTotale }}>
                {/* Griglia drop cells (sfondo) */}
                <Grid container sx={{ minHeight: altezzaTotale }}>
                    {settimane.map((giorni, indexSettimana) => (
                        <Grid key={indexSettimana} size={{ xs: lgSettimana }}>
                            <Grid container sx={{ height: "100%", minHeight: altezzaTotale }}>
                                {giorni.map((giorno) => (
                                    <Grid
                                        key={giorno.toISODate()}
                                        size={{ xs: 12 / 5 }}
                                        sx={{ minHeight: altezzaTotale }}
                                    >
                                        <GanttDropCell
                                            giorno={giorno}
                                            onOdPDrop={handleOdPDrop}
                                            onFaseMove={handleFaseMove}
                                            onFaseResize={handleFaseResize}
                                            onFrazioneMove={handleFrazioneMove}
                                            onVariazioneCreate={onVariazioneCreate}
                                            onVariazioneMove={handleVariazioneMove}
                                            onVariazioneResize={handleVariazioneResize}
                                            minHeight={altezzaTotale}
                                            isLavorativo={calendarioMap[giorno.toISODate() || ""]?.is_lavorativo ?? true}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    ))}
                </Grid>

                {/* Overlay banda placeholder (in cima) */}
                {layoutVariazioni.length > 0 && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: altezzaBandaVariazioni,
                            pointerEvents: "none",
                            backgroundColor: theme.palette.info.light + "10",
                            borderBottom: `1px dashed ${theme.palette.divider}`,
                        }}
                    >
                        {layoutVariazioni.map(({ variazione, top }) => {
                            const { isVisible, left, width } = calcolaLeftWidth(
                                variazione.data_da,
                                variazione.data_a,
                            );
                            if (!isVisible) return null;
                            return (
                                <GanttVariazioneBar
                                    key={variazione.id}
                                    variazione={variazione}
                                    coloreReparto={coloreByRepartoId[variazione.reparto]}
                                    top={top}
                                    left={left}
                                    width={width}
                                    onClick={onVariazioneClick}
                                />
                            );
                        })}
                    </Box>
                )}

                {/* Overlay frazioni (barre) — shiftato sotto la banda placeholder */}
                <Box
                    sx={{
                        position: "absolute",
                        top: altezzaBandaVariazioni,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        pointerEvents: "none",
                    }}
                >
                    {layoutFrazioni.map(({ frazione, offsetTop }) => (
                        <GanttFrazioneBar
                            key={frazione.id}
                            frazione={frazione}
                            dataInizioGantt={dataInizioGantt}
                            totaleGiorni={totaleGiorni}
                            offsetTop={offsetTop}
                            onFaseClick={onFaseClick}
                            onFrazioneDelete={onFrazioneDelete}
                            onFaseDelete={onFaseDelete}
                            onFaseEdit={onFaseEdit}
                            isAttivo={odpAttivoId === frazione.odp}
                            onSelect={onOdPSelect}
                            repartiSelezionati={repartiSelezionati}
                            operatoriSelezionati={operatoriSelezionati}
                        />
                    ))}
                </Box>

                {/* Empty state */}
                {frazioniFiltrate.length === 0 && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            pointerEvents: "none",
                        }}
                    >
                        <Typography variant="body1" color="text.secondary">
                            Trascina un OdP dalla spalla destra per schedulare
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default SchedulazioneGanttTimeline;